<template>
  <v-layout row align-center class="fab-container" v-if="form">

    <v-menu
      v-if="isPreview && alternativePreviewUrls.length > 0 && isVideoTranscoded"
      class="d-inline"
      v-model="menu"
      content-class="currentDateContainer"
      :close-on-content-click="false"
      :nudge-bottom="5"
      color="accent"
      lazy
      transition="scale-transition"
      offset-y
      dark
    >
      <template v-slot:activator="{ on }">
        <v-btn v-on="on" color="primary">
          <v-icon left>visibility</v-icon>
          {{ selectedPreview }}
        </v-btn>
      </template>
      <v-list>
        <template v-for="(item, index) in alternativePreviewUrls">
          <v-list-tile
            :key="index"
            @click="selectPreview(item.previewUrl)"
          >
            <v-list-tile-title>{{ item.name }}</v-list-tile-title>
          </v-list-tile>
        </template>
      </v-list>
    </v-menu>

    <v-tooltip bottom>
      <v-fab-transition slot="activator">
        <v-btn fab
               class="hidden-xs-only"
               color="primary"
               @click="togglePreviewMode"
               v-show="!disabled && previewUrl && isVideoTranscoded">
          <v-icon v-html="togglePreviewIcon"></v-icon>
        </v-btn>
      </v-fab-transition>
      <span v-html="togglePreviewText"></span>
    </v-tooltip>

    <nx-round-button
      :color="saveColor"
      :disabled="disabled"
      :error="numMandatoryFieldsErrors + numInvalidValueFields"
      :loading="loading"
      @click.native="save"
      icon="save"
      v-authorized="{ action: 'update', entity: 'content', id }"
      v-if="isDirty && !hasScheduledPublication && (isVideoTranscoded || !publishOnVideoTranscoded)"
    ></nx-round-button>

    <nx-round-button
      :color="publishColor"
      :disabled="disabled"
      :error="numErrors"
      :loading="loading"
      @click.native="schedule"
      icon="alarm"
      v-else-if="isDirty && hasScheduledPublication"
    ></nx-round-button>

    <nx-round-button
      :color="publishColor"
      :disabled="disabled"
      :error="numErrors"
      :loading="loading"
      @click.native="publish"
      icon="publish"
      v-authorized="{ action: 'publish', entity: 'content', id }"
      v-else-if="(canPublish && hasRequiredForPublishErrors) || (canPublish && isVideoTranscoded)"
    ></nx-round-button>

    <nx-round-button
      color="warning"
      :disabled="disabled"
      :error="numErrors"
      :loading="loading"
      @click.native="showDialog = true;"
      icon="publish"
      v-authorized="{ action: 'publish', entity: 'content', id }"
      v-else-if="canPublish && !isVideoTranscoded && !publishOnVideoTranscoded"
    ></nx-round-button>

    <nx-dialog-confirm
      v-if="confirmToPublish"
      :title="$t('message.content.tools.publish')"
      :text="$t('message.content.tools.confirmPublication')"
      :cancel-text="$t('message.content.tools.cancelPublishConfirmation')"
      :accept-text="$t('message.content.tools.acceptPublishConfirmation')"
      :persistent="true"
      accept-color="accent"
      cancel-color="error"
      ref="confirmPublication"
    ></nx-dialog-confirm>

    <nx-round-button
      color="warning"
      :disabled="disabled"
      :error="numErrors"
      :loading="loading"
      @click.native="showDialog = true;"
      icon="file_download"
      v-authorized="{ action: 'publish', entity: 'content', id }"
      v-else-if="canPublish && !isVideoTranscoded && publishOnVideoTranscoded"
    ></nx-round-button>

    <nx-simple-dialog
      :title="$t(`message.content.editor.video.transcoding.${publishOnVideoTranscoded
      ? 'unpublishTranscoding' : 'publishTranscoding'}`)|capitalize"
      :shown="showDialog"
      @close="showDialog = false"
    >
      <span
        class="text-xs-center"
        v-html="$tc(`message.content.editor.video.transcoding.${publishOnVideoTranscoded
      ? 'infoTranscodingUnpublish' : 'infoTranscodingPublish'}`)"
      ></span>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="accent"
          flat
          @click="publishOnVideoTranscoded ? unpublish() : publish(); showDialog = false"
          :loading="loading"
        >{{ $t('message.common.accept') }}</v-btn>
        <v-btn
          color="red"
          flat
          @click="showDialog = false"
          :disabled="loading"
        >{{ $t('message.common.cancel') }}</v-btn>
      </v-card-actions>
    </nx-simple-dialog>
  </v-layout>
</template>


<script>
import Storable from '@/mixins/storable';
import FullscreenDialog from '@/components/dialogs/FullscreenDialog';
import Loadable from '@/mixins/loadable';
import Button from '@/components/buttons/RoundButton';
import SimpleDialog from '@/components/dialogs/SimpleDialog';
import NxDialogConfirm from '@/components/dialogs/Confirm';
import { mapGetters } from 'vuex';
import api from '@/modules/content/editor/api';
import { toArray } from '@/utils';

export default {
  name: 'nx-content-editor-header-video-button',
  mixins: [Storable, Loadable],
  inject: {
    $getParentEditor: {
      default: null,
    },
  },
  components: {
    [Button.name]: Button,
    [FullscreenDialog.name]: FullscreenDialog,
    [SimpleDialog.name]: SimpleDialog,
    NxDialogConfirm,
  },
  data() {
    return {
      preview: false,
      previewViewport: 'xs12',
      menu: false,
      showDialog: false,
    };
  },
  props: {
    disabled: Boolean,
  },
  computed: {
    storePath() {
      return this.$getParentEditor().storePath;
    },
    channelActionConfigByActionName() {
      return this.getters.channelActionConfigByActionName;
    },
    hasScheduledPublication() {
      return this.getters.hasScheduledPublication;
    },
    form() {
      return this.getters.form;
    },
    formFields() {
      return this.getters.formFields;
    },
    formErrors() {
      return this.getters.formErrors;
    },
    draft() {
      return this.getters.draft;
    },
    mandatoryFieldsErrors() {
      return this.getters.mandatoryFieldsErrors;
    },
    requiredForPublishErrors() {
      return this.getters.requiredForPublishErrors;
    },
    previewUrl() {
      return this.getters.previewUrl;
    },
    alternativePreviewUrls() {
      return this.getters.alternativePreviewUrls;
    },
    publishOnVideoTranscoded() {
      return this.getters.publishOnVideoTranscoded;
    },
    isVideoTranscoded() {
      return this.getters.isVideoTranscoded;
    },
    confirmToPublish() {
      return this.getters.confirmToPublish;
    },
    isDraft() {
      return this.state.isDraft;
    },
    isDirty() {
      return this.state.isDirty;
    },
    type() {
      return this.state.type;
    },
    currentChannel() {
      return this.state.currentChannel;
    },
    currentLanguage() {
      return this.state.currentLanguage;
    },
    isPreview() {
      return this.state.isPreview;
    },
    id() {
      if (this.$getParentEditor()) {
        return this.$getParentEditor().id;
      }
      return parseInt(this.$route.params.id, 10);
    },
    relativeVersionId() {
      const relativeVersionId = this.$route.params.relativeVersionId;
      if (relativeVersionId) {
        return parseInt(relativeVersionId, 10);
      }
      return null;
    },
    numMandatoryFieldsErrors() {
      return this.mandatoryFieldsErrors.length;
    },
    hasMandatoryFieldsErrors() {
      return this.numMandatoryFieldsErrors > 0;
    },
    // Required fields with an empty value
    // TODO: review this logic
    invalidFields() {
      // Using .valid prop because .invalid prop not updating correctly when .valid is false
      return Object.keys(this.formFields).filter(
        key => this.formFields[key].valid === false,
      );
    },
    hasInvalidValueFields() {
      return this.invalidFields.length > 0;
    },
    numInvalidValueFields() {
      return this.invalidFields.length;
    },
    // Fields with value that cannot be saved because a wrong value:
    // I.E: invalid email, invalid relation card.
    invalidValueFields() {
      return this.formErrors.filter(error => error.rule !== 'required');
    },
    numErrors() {
      return this.invalidFields.length;
    },
    hasErrors() {
      return this.numErrors > 0 || this.form.invalid;
    },
    hasRequiredForPublishErrors() {
      return this.requiredForPublishErrors.length > 0;
    },
    hasSaveErrors() {
      return this.hasMandatoryFieldsErrors || this.hasInvalidValueFields;
    },
    saveColor() {
      return this.hasSaveErrors ? 'error' : 'accent';
    },
    publishColor() {
      return this.hasErrors || this.hasRequiredForPublishErrors ? 'error' : 'accent';
    },
    previewTitle() {
      return `${this.currentChannel} preview`;
    },
    togglePreviewIcon() {
      return this.isPreview ? 'edit' : 'visibility';
    },
    togglePreviewText() {
      return this.isPreview ? this.$t('message.content.editor.edit') : this.$t('message.content.editor.preview');
    },
    selectedPreview() {
      const currentPreview = this.alternativePreviewUrls.find(
        preview => preview.previewUrl === this.previewUrl,
      );
      if (currentPreview) {
        return currentPreview.name;
      }
      return null;
    },
    ...mapGetters([
      'features',
    ]),
    canPublish() {
      if (!this.actionIsAllowed('publish')) {
        return false;
      }
      return !this.hasScheduledPublication && this.isDraft;
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeyDown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown);
  },
  methods: {
    actionIsAllowed(action) {
      const config = this.channelActionConfigByActionName(action);
      if (config
        && config.allowed === false) {
        return false;
      }
      return true;
    },
    selectPreview(previewUrl) {
      console.log(`Selected ${previewUrl}`);
      this.dispatch('FORCE_PREVIEW_URL', previewUrl);
      this.menu = false;
    },
    reloadPreview() {
      // This reload works even crossdomain src!!
      if (this.$refs.iframe) this.$refs.iframe.src = this.$refs.iframe.src;
    },
    async save() {
      if (this.hasSaveErrors) {
        await this.validateAndShowErrors();
      } else {
        this.loadingStart();
        await this.dispatch('SAVE');
        this.$emit('save');
        this.reloadPreview();
        this.loadingFinish();
      }
    },
    async schedule() {
      if (this.hasRequiredForPublishErrors) {
        this.validateForPublishAndShowErrors();
        return;
      }
      if (this.hasErrors) {
        this.validateAndShowErrors();
        return;
      }
      this.save();
    },
    async confirmPublication() {
      if (!await this.verifyPublicationUrl()) {
        return false;
      }
      if (this.features.SHOW_MODAL_CONFIRM_PUBLISH_WITHOUT_TAGS
        && this.draftHasTags // video, for ERU, does not have this function. Better safe than sorry
        && !this.draftHasTags()) {
        const result = await this.$refs.confirmContentTags.open();

        if (!result) {
          this.scrollToField('cataloging', 'tagIds');
          return false;
        }
      }

      if (this.confirmToPublish) {
        const confirm = await this.$refs.confirmPublication.open();
        if (!confirm) {
          return false;
        }
      }

      if (this.features.SHOW_MODAL_PUBLISH_NON_PUBLISHED_RELATED_CONTENTS) {
        return this.continueWithUnpublishedContents('publish');
      }

      return true;
    },
    async continueWithUnpublishedContents(action) {
      try {
        this.unpublishedContents = [];
        const {
          contents,
          contentsWithValidationErrors,
          contentsWithoutPermissionToPublish,
        } = await api.unpublishedContents(this.id, this.state.relativeVersionId);

        this.unpublishedContents = toArray(contents);
        this.unpublishedContentsWithValidationErrors = toArray(contentsWithValidationErrors);
        this.unpublishedContentsWithoutPermissionToPublish = toArray(contentsWithoutPermissionToPublish);

        if (this.hasUnpublishedContents) {
          this.publishAction = action;
          return this.$refs.unpublishedContents.open({ width: 500 });
        }
      } catch (e) {
        console.error(e);
        // Show modal telling user that something went wrong but can still publish if they wishes
        // TODO: return this.$refs.confirmPublicationAfterApiError.open();
      }

      return true;
    },
    async verifyPublicationUrl() {
      if (this.isPublished && this.publicUrlHasChanged) {
        return this.$refs.confirmUrlChange.open();
      }

      return true;
    },
    async publish() {
      if (this.hasRequiredForPublishErrors) {
        this.validateForPublishAndShowErrors();
        return;
      }
      if (this.hasErrors) {
        this.validateAndShowErrors();
        return;
      }
      this.loadingStart();
      const publicationConfirmed = await this.confirmPublication();
      if (publicationConfirmed) {
        const { latestRelativeVersionId } = await this.dispatch('PUBLISH');
        if (latestRelativeVersionId) {
          this.$emit('updateRelativeVersionId', latestRelativeVersionId);
        } else if (this.publishOnVideoTranscoded) {
          this.reloadPreview();
        }
      }
      this.loadingFinish();
    },
    async validateAndShowErrors() {
      this.$emit('validate');
    },
    async validateForPublishAndShowErrors() {
      this.$emit('validate:publish');
    },
    async togglePreviewMode() {
      await this.dispatch('TOGGLE_PREVIEW_MODE');
    },
    async unpublish() {
      this.loadingStart();
      const { latestRelativeVersionId } = await this.dispatch('UNPUBLISH');
      if (latestRelativeVersionId) {
        this.$emit('updateRelativeVersionId', latestRelativeVersionId);
      } else {
        await this.dispatch('INIT_EDITOR', {
          id: this.id,
          relativeVersionId: this.relativeVersionId,
          type: this.type,
          channel: this.currentChannel,
          language: this.currentLanguage,
        });
      }
      this.loadingFinish();
    },
    onKeyDown(event) {
      if (event.ctrlKey || event.metaKey) {
        switch (String.fromCharCode(event.which).toLowerCase()) {
          // If ctrl+s was pressed...
          case 's':
            event.preventDefault();
            if (
              !this.disabled
              && this.isDirty
              && !this.hasScheduledPublication
            ) {
              this.save();
            }
            break;
          default:
          // None by default
        }
      }
    },
  },
  watch: {
    previewUrl(url) {
      if (!url && this.isPreview) {
        this.togglePreviewMode();
      }
    },
    currentChannel(channel) {
      if (channel !== 'master') {
        // Set the preview to mobile
        this.previewViewport = 'xs4';
      } else {
        this.previewViewport = 'xs12';
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
.fab-container {
  position: absolute;
  bottom: -32px;
  right: 0;
  padding-right: 18px;
}

.preview {
  width: 100%;
  height: 100vh;
}
</style>
